import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Shield, Heart, Phone, Users, Lock, CheckCircle } from "lucide-react";

const SafetyAndEthics = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="va-blue-bar h-1"></div>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="hover:bg-muted"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-8 w-auto" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Page Title */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
            Safety & Ethics
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            VALORLink is built on the belief that support should be human, ethical, and transparent — especially during difficult moments.
          </p>
          <p className="text-muted-foreground mt-4">
            This page explains what VALORLink does, what it does not do, and how we approach safety and responsibility.
          </p>
        </div>

        <div className="space-y-10">
          {/* What VALORLink Is */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Heart className="w-5 h-5 text-primary" />
              What VALORLink Is
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6 space-y-4 text-muted-foreground">
                <p>
                  VALORLink provides peer-to-peer support by connecting veterans with trained fellow veterans.
                  The platform is designed to reduce isolation, offer understanding, and help users navigate next steps and available resources.
                </p>
                <p>
                  VALORLink is intended to complement existing public and nonprofit support systems, not replace them.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* What VALORLink Is Not */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <span className="w-5 h-5 flex items-center justify-center text-red-500 font-bold">✕</span>
              What VALORLink Is Not
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6 space-y-4 text-muted-foreground">
                <p>VALORLink is not:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>A clinical or therapeutic service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>A provider of medical or mental health care</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>A diagnostic or treatment platform</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>An emergency response system</span>
                  </li>
                </ul>
                <p className="pt-2 border-t border-border">
                  Use of VALORLink does not create a medical, therapeutic, or clinician-client relationship.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Safety First */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Shield className="w-5 h-5 text-primary" />
              Safety First
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6 space-y-4 text-muted-foreground">
                <p>
                  When a user appears to be in immediate danger or distress, VALORLink emphasizes connection to appropriate emergency and crisis resources.
                </p>
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mt-4">
                  <p className="font-semibold text-foreground mb-3">
                    If you or someone else is in immediate danger, please contact:
                  </p>
                  <div className="space-y-3">
                    <a 
                      href="tel:911" 
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-border hover:border-destructive transition-colors"
                    >
                      <Phone className="w-5 h-5 text-destructive" />
                      <div>
                        <p className="font-medium text-foreground">Emergency Services</p>
                        <p className="text-lg font-bold text-destructive">911</p>
                      </div>
                    </a>
                    <a 
                      href="tel:988" 
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-border hover:border-primary transition-colors"
                    >
                      <Phone className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium text-foreground">Veterans Crisis Hotline</p>
                        <p className="text-lg font-bold text-primary">988 (Press 1)</p>
                      </div>
                    </a>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    These resources are available 24 hours a day.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Volunteer Boundaries */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Users className="w-5 h-5 text-primary" />
              Volunteer Boundaries
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6 space-y-4 text-muted-foreground">
                <p>
                  VALORLink volunteers provide presence, empathy, and peer support.
                  They do not provide clinical advice, diagnosis, or treatment, and they do not promise outcomes.
                </p>
                <p>
                  Clear role boundaries exist to protect both users and volunteers.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Ethical Commitments */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <CheckCircle className="w-5 h-5 text-primary" />
              Ethical Commitments
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6">
                <p className="text-muted-foreground mb-4">VALORLink is committed to:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Transparency about scope and limitations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Respect for user dignity and autonomy</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Privacy and responsible data handling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Avoiding exploitation, coercion, or misleading claims</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Continuous review of safety and ethical practices</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Data & Privacy */}
          <section>
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Lock className="w-5 h-5 text-primary" />
              Data & Privacy
            </h2>
            <Card className="bg-white border-border">
              <CardContent className="p-6 space-y-4 text-muted-foreground">
                <p>
                  VALORLink collects only the information necessary to provide peer support and maintain platform safety.
                  User data is not sold.
                  Information may be reviewed internally if there is a safety concern or incident requiring follow-up.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Closing Statement */}
          <section className="pt-6 border-t border-border">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground leading-relaxed">
                  Support works best when it is honest, respectful, and clearly defined.
                  VALORLink exists to provide connection — while ensuring users know when and how to access professional or emergency care.
                </p>
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <Button 
            variant="outline" 
            onClick={() => navigate("/")}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-border mt-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
                <span className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground text-center sm:text-right">
                © 2025 V.A.L.O.R.™ Veterans' Alliance for Legislative & Organizational Reform, Inc.
              </p>
            </div>
            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                <strong>Disclaimer:</strong> VALORLink provides peer support only and does not offer medical, mental health, or emergency services.
                If you are in immediate danger, please contact <a href="tel:911" className="text-primary hover:underline font-medium">911</a> or the 
                Veterans Crisis Hotline (<a href="tel:988" className="text-primary hover:underline font-medium">988</a>, Press 1).
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SafetyAndEthics;
